import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'oidc-react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import {
  Tooltip,
  AgronodButton as Button,
  AgronodTypography as Typography,
  AgronodAlert as Alert,
  AgronodCard,
  AgronodChip,
} from '@agronod/mui-components';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { AdminService } from '@/services/admin.service';
import { Issuer, UpdateIssuerRequest, CreateIssuerRequest } from '@/types';
import { useSnackbar } from 'notistack';
import { useApiClient } from '@/hooks/useApiClient';
import CreateIssuerModal from '@/components/admin/CreateIssuerModal';
import EditIssuerModal from '@/components/admin/EditIssuerModal';

/**
 * AdminIssuersPage - Manage certification issuers (organizations)
 *
 * Features:
 * - List all issuers with pagination
 * - Filter to show/hide inactive issuers
 * - Create new issuers
 * - Edit existing issuers
 * - Delete issuers (only if no associated control types)
 * - Activate/deactivate issuers
 * - Display statistics (total issuers, active issuers)
 */
const AdminIssuersPage: React.FC = () => {
  useApiClient(); // Inject current OIDC token into API client
  const { userData } = useAuth(); // Get OIDC authentication state
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    includeInactive: false,
    page: 1,
    limit: 10,
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedIssuer, setSelectedIssuer] = useState<Issuer | null>(null);

  // Fetch issuers - only when authenticated
  const {
    data: issuersData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['admin', 'issuers', filters],
    queryFn: () => AdminService.getIssuers(filters.includeInactive),
    enabled: !!userData?.access_token, // Wait for OIDC token before making request
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: AdminService.deleteIssuer,
    onSuccess: () => {
      enqueueSnackbar('Issuer raderad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'issuers'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid radering', { variant: 'error' });
    },
  });

  // Toggle active mutation (activate/deactivate)
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive ? AdminService.activateIssuer(id) : AdminService.deactivateIssuer(id),
    onSuccess: (data) => {
      enqueueSnackbar(
        data.isActive ? 'Issuer aktiverad' : 'Issuer deaktiverad',
        { variant: 'success' }
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'issuers'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid statusändring', { variant: 'error' });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: AdminService.createIssuer,
    onSuccess: () => {
      enqueueSnackbar('Issuer skapad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'issuers'] });
      setIsCreateModalOpen(false);
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid skapande', { variant: 'error' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIssuerRequest }) =>
      AdminService.updateIssuer(id, data),
    onSuccess: () => {
      enqueueSnackbar('Issuer uppdaterad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'issuers'] });
      setIsEditModalOpen(false);
      setSelectedIssuer(null);
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid uppdatering', { variant: 'error' });
    },
  });

  // Handle API response structure
  const issuers = issuersData?.issuers || [];
  const totalIssuers = issuersData?.total || 0;

  // Calculate stats from actual data
  const stats = {
    totalIssuers: totalIssuers,
    activeIssuers: issuers.filter(i => i.isActive).length,
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 })); // MUI uses 0-based, backend uses 1-based
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleIncludeInactiveChange = (includeInactive: boolean) => {
    setFilters(prev => ({ ...prev, includeInactive, page: 1 }));
  };

  const handleDeleteIssuer = (issuer: Issuer) => {
    if (issuer._count && issuer._count.controlTypes > 0) {
      enqueueSnackbar(
        `Kan inte radera issuer med ${issuer._count.controlTypes} kontrolltyper. Deaktivera istället.`,
        { variant: 'warning' }
      );
      return;
    }

    if (window.confirm(`Är du säker på att du vill radera "${issuer.displayName}"?`)) {
      deleteMutation.mutate(issuer.id);
    }
  };

  const handleEditIssuer = (issuer: Issuer) => {
    setSelectedIssuer(issuer);
    setIsEditModalOpen(true);
  };

  const handleToggleActive = (issuer: Issuer) => {
    const action = issuer.isActive ? 'deaktivera' : 'aktivera';
    if (window.confirm(`Är du säker på att du vill ${action} "${issuer.displayName}"?`)) {
      toggleActiveMutation.mutate({ id: issuer.id, isActive: !issuer.isActive });
    }
  };

  const handleCreateIssuer = async (data: CreateIssuerRequest) => {
    await createMutation.mutateAsync(data);
  };

  const handleUpdateIssuer = async (id: string, data: UpdateIssuerRequest) => {
    await updateMutation.mutateAsync({ id, data });
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3
        }}>
        <Typography variant="h4" component="h1">
          Issuers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Skapa Issuer
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{
        mb: 4
      }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <AgronodCard>
            <Typography color="textSecondary" gutterBottom>
              Totalt antal issuers
            </Typography>
            <Typography variant="h4">
              {stats.totalIssuers}
            </Typography>
          </AgronodCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <AgronodCard>
            <Typography color="textSecondary" gutterBottom>
              Aktiva issuers
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats.activeIssuers}
            </Typography>
          </AgronodCard>
        </Grid>
      </Grid>

      {/* Issuers Table */}
      <AgronodCard>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2
            }}>
            <Typography variant="h6">
              Issuers
            </Typography>
          </Box>

          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              flexWrap: "wrap"
            }}>
            <Button
              variant={filters.includeInactive ? "contained" : "outlined"}
              onClick={() => handleIncludeInactiveChange(!filters.includeInactive)}
              size="small"
            >
              Visa inaktiva
            </Button>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4
              }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Laddar issuers...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Kunde inte ladda issuers: {(error as Error).message}
            </Alert>
          ) : issuers.length === 0 ? (
            <Alert severity="info">
              Inga issuers hittades.
            </Alert>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 'calc(100vh - 400px)',
                overflow: 'auto'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Control Types</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Senast uppdaterad</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Åtgärder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issuers.map((issuer) => (
                    <TableRow key={issuer.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {issuer.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {issuer.displayName}
                        </Typography>
                        {issuer.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {issuer.description.length > 50
                              ? `${issuer.description.substring(0, 50)}...`
                              : issuer.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <AgronodChip
                          label={issuer.isActive ? 'Aktiv' : 'Inaktiv'}
                          size="small"
                          color={issuer.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {issuer._count?.controlTypes || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(issuer.updatedAt).toLocaleDateString('sv-SE')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center"
                          }}>
                          <Tooltip title="Redigera">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditIssuer(issuer)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={issuer.isActive ? 'Deaktivera' : 'Aktivera'}>
                            <span>
                              <IconButton
                                size="small"
                                color={issuer.isActive ? 'warning' : 'success'}
                                onClick={() => handleToggleActive(issuer)}
                                disabled={toggleActiveMutation.isPending}
                              >
                                {issuer.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Ta bort">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteIssuer(issuer)}
                                disabled={deleteMutation.isPending || (issuer._count?.controlTypes || 0) > 0}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {issuers.length > 0 && (
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1 }}>
              <TablePagination
                component="div"
                count={totalIssuers}
                page={filters.page - 1} // MUI uses 0-based, backend uses 1-based
                onPageChange={handlePageChange}
                rowsPerPage={filters.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rader per sida:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} av ${count !== -1 ? count : `mer än ${to}`}`
                }
                sx={{
                  '& .MuiTablePagination-selectLabel': { fontSize: '0.875rem' },
                  '& .MuiTablePagination-displayedRows': { fontSize: '0.875rem' }
                }}
              />
            </Box>
          )}
      </AgronodCard>

      {/* Create Issuer Modal */}
      <CreateIssuerModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateIssuer}
        isLoading={createMutation.isPending}
      />

      {/* Edit Issuer Modal */}
      <EditIssuerModal
        open={isEditModalOpen}
        issuer={selectedIssuer}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedIssuer(null);
        }}
        onSubmit={handleUpdateIssuer}
        isLoading={updateMutation.isPending}
      />
    </Box>
  );
};

export default AdminIssuersPage;
